import { memo, useCallback, useState } from 'react';
import { Box, CircularProgress, styled } from '@mui/material';
import { flexRender, type TableOptions, type Row } from '@tanstack/react-table';
import { useFormContext, type UseFormReturn } from 'react-hook-form';

import { Checkbox } from 'components/mui/common/Checkbox';
import { getUIDefinitionIconStyles } from 'utils/styles';
import { shortenText } from 'utils/common';
import { useTableManagement } from 'contexts/TableManagementContext';
import { TableRowHeliosFunctionsControls } from '../TableRowHeliosFunctionsControls';
import { TableRowControls } from '../TableRowControls';
import { getHeliosCell } from 'utils/table/user/helios/heliosCell';
import type { TableFormData } from 'components/common/Table/Table';
import type { DataSchema, HeliosRowControls, HeliosRowFunctionsControls, Schema } from 'types';
import type { ReturnRow } from 'types/table';
import { getFormFieldName } from '../editableComponentFactory';
import { TableCellEditHelios } from '../TableCellEditHelios';
import { Button } from 'components/mui/common/Button';
import { useTranslation } from 'react-i18next';
import type { RowData } from 'utils/helios/row';
import { transformRowFieldData } from 'utils/helios/row';
import { TABLE_CELL_TYPE } from 'types/enums';
import { toast } from 'react-toastify';
import { validateRequiredFieldsAndGetErrors } from 'utils/hooks/userAgendaTable/useHeliosAgendaTableActions';
import { replaceKey } from 'utils/formItemsDots';
import { BUTTONS_GROUP_ATTRIBUTE_KEY } from 'utils/agendaRecord/record';
import { sanitizeRichText } from 'utils/quill';
import { HeliosRichTextMobile } from '../HeliosRichText.styles';
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardRow,
  CellLabel,
  CellValueWrapper,
  EditButtons,
  EditLabelTypography,
  TextContainer,
  Wrapper,
} from './mobileTableStyles';

interface MobileHeliosTableProps<T extends keyof P, P extends DataSchema> {
  rows: Row<Schema<T, P>>[];
  dataSchema: P;
  selectAllForm: UseFormReturn<TableFormData<boolean>>;
  hideCheckbox?: boolean;
  selectedRows: string[];
  heliosRowControls?: HeliosRowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowFunctionsControls?: HeliosRowFunctionsControls[];
  editRowId?: string[] | null;
  onRowClick?: (row: Schema<T, P>) => void;
  handleRowSelect: (identifier: string, isChecked: boolean) => void;
  onSaveEditedRow?: ReturnRow<P>;
  onCancelEditRow?: (id: string) => void;
  testIdPrefix?: string;
}

const NoLabelCardRow = styled('div')({
  gridColumn: '1 / -1',
  minWidth: 0,
});

const MobileHeliosTableBase = <T extends keyof P, P extends DataSchema>({
  rows,
  dataSchema,
  hideCheckbox,
  selectedRows,
  selectAllForm,
  heliosRowControls,
  heliosRowFunctionsControls,
  editRowId,
  handleRowSelect,
  onRowClick,
  onSaveEditedRow,
  onCancelEditRow,
  testIdPrefix,
}: MobileHeliosTableProps<T, P>) => {
  const { t } = useTranslation();
  const { setValue: selectAllSetValue } = selectAllForm;
  const { control, handleSubmit: onSubmit, setValue, setError, getValues, formState } = useFormContext();
  const { shouldShowRowControls, hasHeliosFunctions, isCellEditable, isRowNew, hasEditableActions, showSaveButton, showCancelEditButton } =
    useTableManagement();
  const [savingRowId, setSavingRowId] = useState<string | null>(null);

  const noEdits = useCallback(() => {
    toast.error(t('common.noEdits'));
  }, [t]);

  const handleSubmit = useCallback(
    (row: Row<Schema<T, P>>) => {
      const rowId = row.original.identifier;
      const formValues = getValues();
      const values = formValues ? formValues[rowId] : undefined;

      if (!values) {
        noEdits();
        return;
      }

      const keys = Object.keys(values);

      const dirtyFields: Record<string, Record<string, boolean | undefined>> = {
        [rowId]: Object.fromEntries(keys.map((key) => [key, true])),
      };
      const formData: Record<string, Record<string, unknown>> = { [rowId]: values };
      const originalData = rows.map((r) => r.original);

      const errorMessage = validateRequiredFieldsAndGetErrors(dirtyFields, formData, dataSchema, originalData, setError, t);
      if (errorMessage) {
        toast.error(errorMessage, { style: { whiteSpace: 'pre-line' } });
        return;
      }

      setSavingRowId(rowId);

      onSubmit(() => {
        const data: RowData<P> = {} as RowData<P>;

        keys.forEach((key) => {
          const value = values[key];
          data[key as keyof P] = transformRowFieldData(key, value, dataSchema);
        });

        if (onSaveEditedRow) {
          const done = onSaveEditedRow({
            ...data,
            id: {
              value: row.original.id,
              type: TABLE_CELL_TYPE.ID,
            },
          }) as Promise<void> | void;
          done &&
            done.then &&
            done.then(() => {
              setSavingRowId(null);
            });
        }
      })();
    },
    [dataSchema, getValues, onSaveEditedRow, onSubmit, rows, setError, t, noEdits]
  );

  const handleEdit = useCallback(
    (row: Row<Schema<T, P>>) => {
      const rowId = row.original.identifier;
      const formValues = getValues();
      const valuesDirty = formState.dirtyFields[rowId];
      const values = formValues ? formValues[rowId] : undefined;

      if (!values) {
        noEdits();
        return;
      }
      if (!valuesDirty || Object.keys(valuesDirty).length === 0) {
        noEdits();
        return;
      }

      const keys = Object.keys(valuesDirty);

      const dirtyFields: Record<string, Record<string, boolean | undefined>> = { [rowId]: valuesDirty };
      const formData: Record<string, Record<string, unknown>> = { [rowId]: values };
      const originalData = rows.map((r) => r.original);

      const errorMessage = validateRequiredFieldsAndGetErrors(dirtyFields, formData, dataSchema, originalData, setError, t);
      if (errorMessage) {
        toast.error(errorMessage, { style: { whiteSpace: 'pre-line' } });
        return;
      }

      setSavingRowId(rowId);

      onSubmit(() => {
        const data: RowData<P> = {} as RowData<P>;

        keys.forEach((key) => {
          const value = values[key];
          data[key as keyof P] = transformRowFieldData(key, value, dataSchema);
        });

        if (onSaveEditedRow) {
          const done = onSaveEditedRow({
            ...data,
            id: {
              value: row.original.identifier,
              type: TABLE_CELL_TYPE.ID,
            },
          }) as Promise<void> | void;
          done &&
            done.then &&
            done.then(() => {
              setSavingRowId(null);
            });
        }
      })();
    },
    [dataSchema, formState, getValues, onSaveEditedRow, onSubmit, rows, setError, t, noEdits]
  );

  const handleRowClick = useCallback(
    (row: Row<Schema<T, P>>) => {
      const isBulkSelectionActive = selectedRows.length > 0 && !hideCheckbox;

      if (isBulkSelectionActive) {
        const selectedId = row.original.identifier;
        const isSelected = selectedRows.includes(selectedId);

        handleRowSelect(selectedId, !isSelected);

        if (isSelected) {
          selectAllSetValue('selectAll', false, {
            shouldDirty: true,
          });
        }

        return;
      }

      onRowClick?.(row.original);
    },
    [handleRowSelect, hideCheckbox, onRowClick, selectAllSetValue, selectedRows]
  );

  return (
    <Wrapper>
      {rows.map((row) => {
        const isNew = isRowNew(row.original.identifier);
        const showSaveRowButton = showSaveButton(row.original.identifier);
        const showCancelEditRowButton = showCancelEditButton(row.original.identifier);

        const isEditingExistingRow = editRowId?.includes(row.original.id) ?? false;
        const isInEditMode = isNew || isEditingExistingRow;
        const isGroupedRow = row.getIsGrouped();
        return (
          <Card
            key={row.id}
            data-testid={testIdPrefix ? `${testIdPrefix}-row-${row.index + 1}` : undefined}
            isEditing={isInEditMode}
            onClick={!isInEditMode ? () => handleRowClick(row) : undefined}>
            {!isInEditMode && (shouldShowRowControls || hasHeliosFunctions || !hideCheckbox) && (
              <CardHeader onClick={(e) => e.stopPropagation()}>
                {!hideCheckbox && (
                  <Checkbox
                    key={row.original.identifier}
                    checked={selectedRows.includes(row.original.identifier)}
                    dataTestId={testIdPrefix ? `${testIdPrefix}-row-${row.index + 1}-checkbox` : undefined}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const selectedId = row.original.identifier;

                      handleRowSelect(selectedId, isChecked);

                      if (!isChecked) {
                        selectAllSetValue('selectAll', false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  />
                )}
                <CardHeaderActions>
                  {shouldShowRowControls && !isGroupedRow && (
                    <TableRowControls
                      noTableCell
                      row={row.original}
                      heliosRowControls={heliosRowControls}
                      sx={{ p: 0 }}
                      testIdPrefix={testIdPrefix}
                    />
                  )}
                  {hasHeliosFunctions && !isGroupedRow && (
                    <TableRowHeliosFunctionsControls
                      testIdPrefix={testIdPrefix}
                      noTableCell
                      row={row.original}
                      controls={heliosRowFunctionsControls}
                      sx={{ p: 0, height: '20px' }}
                    />
                  )}
                </CardHeaderActions>
              </CardHeader>
            )}

            <CardContent isNew={isInEditMode}>
              {row.getVisibleCells().map((cell) => {
                const columnName = cell.column.columnDef.header?.toString() ?? '';
                const headerText = shortenText(columnName, 20);

                const modifiedCell = {
                  ...cell,
                  column: { ...cell.column, id: replaceKey(cell.column.id) },
                };

                const editMode = isCellEditable(row.original.identifier, modifiedCell.column.id);

                const cellKey = getFormFieldName(row, modifiedCell.column.id);

                const {
                  displayValue,
                  valueForRender,
                  uiDefinition,
                  Icon,
                  boxStyles,
                  textStyles,
                  isTextArea,
                  isRichText,
                  valueTextAlign,
                  labelTextAlign,
                } = getHeliosCell({
                  cell,
                  row,
                  dataSchema,
                });

                const isActionCell = cell.column.id === BUTTONS_GROUP_ATTRIBUTE_KEY;
                const isNoLabelCardRow = !headerText;
                const headerTextComponent = isNoLabelCardRow ? null : <CellLabel labelAlign={labelTextAlign}>{headerText}</CellLabel>;
                const RowComponent = isNoLabelCardRow ? NoLabelCardRow : CardRow;

                return isInEditMode ? (
                  <Box key={cellKey}>
                    {!isActionCell && (
                      <EditLabelTypography variant="subtitle2" labelAlign={labelTextAlign}>
                        {headerText}
                      </EditLabelTypography>
                    )}
                    {!editMode ? (
                      flexRender(cell.column.columnDef.cell, {
                        ...cell.getContext(),
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        getValue: () => valueForRender as any,
                      })
                    ) : (
                      <TableCellEditHelios
                        setValue={setValue}
                        row={row}
                        cell={modifiedCell}
                        key={cellKey}
                        control={control}
                        dataSchema={dataSchema}
                        isNew={isNew}
                      />
                    )}
                  </Box>
                ) : (
                  <RowComponent key={cell.id}>
                    {headerTextComponent}

                    <CellValueWrapper uiDefinition={!!uiDefinition} sx={boxStyles} valueAlign={valueTextAlign}>
                      {Icon ? <Icon sx={{ ...getUIDefinitionIconStyles(uiDefinition), flexShrink: 0, marginRight: '6px' }} /> : null}
                      <TextContainer sx={textStyles} valueAlign={valueTextAlign}>
                        {(() => {
                          if (isRichText) {
                            return (
                              <HeliosRichTextMobile>
                                <div
                                  className="ql-editor"
                                  dangerouslySetInnerHTML={{ __html: sanitizeRichText(displayValue ? displayValue.toString() : '') }}
                                />
                              </HeliosRichTextMobile>
                            );
                          }
                          if (isTextArea) {
                            return displayValue ?? '';
                          }
                          return flexRender(cell.column.columnDef.cell, {
                            ...cell.getContext(),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            getValue: () => valueForRender as any,
                          });
                        })()}
                      </TextContainer>
                    </CellValueWrapper>
                  </RowComponent>
                );
              })}
            </CardContent>
            {(hasEditableActions || isEditingExistingRow) && (
              <EditButtons>
                {(showCancelEditRowButton || isEditingExistingRow) && (
                  <Button sx={{ background: 'white' }} variant="outlined" onClick={() => onCancelEditRow?.(row.original.id)}>
                    {t('common.cancel')}
                  </Button>
                )}
                {(showSaveRowButton || isEditingExistingRow) && (
                  <Button
                    variant="contained"
                    disabled={savingRowId === row.original.identifier}
                    onClick={isEditingExistingRow ? () => handleEdit(row) : () => handleSubmit(row)}
                    startIcon={savingRowId === row.original.identifier ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('common.save')}
                  </Button>
                )}
              </EditButtons>
            )}
          </Card>
        );
      })}
    </Wrapper>
  );
};

export const MobileHeliosTable = memo(MobileHeliosTableBase) as typeof MobileHeliosTableBase;
